import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';

import Table from '../../../components/Table';

import CommitCiIndicator from './CommitCiIndicator';

/**
 * Structural shape of a commit row as rendered by `CommitsBlock`. Both
 * `adminGetReleasePreview` and `adminGetReleaseChangeLog` return commits
 * matching this shape (they share the `ReleaseCommit` GraphQL type), so
 * the component is decoupled from any specific query's generated type.
 */
export interface IReleaseCommitRow {
  sha: string;
  shortSha: string;
  subject: string;
  author?: string | null;
  authoredAt: string;
  prNumber?: number | null;
  prUrl?: string | null;
  /** `success` / `failure` / `pending` / `unknown`; drives the CI column's glyph. */
  ciConclusion: string;
}

interface ICommitsBlockProps {
  commits: IReleaseCommitRow[];
  baseTag: string;
}

/**
 * Expanded-by-default commits-since-last-release table, collapsible for when
 * the list is long enough to bury what is below it.
 *
 * Rendered newest-first. `commits` arrives in GitHub's `/compare` order,
 * which is oldest-first — the reverse of how the release itself is read,
 * where what landed most recently is what an operator is deciding about.
 */
const CommitsBlock = ({ commits, baseTag }: ICommitsBlockProps) => {
  const [expanded, setExpanded] = React.useState(true);
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-fg-tertiary hover:text-fg-secondary flex items-center gap-2 text-xs font-medium uppercase tracking-wider transition-colors"
      >
        <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
        <span>Commits ({commits.length})</span>
        <span className="text-fg-quaternary normal-case tracking-normal">since {baseTag}</span>
      </button>
      {expanded &&
        (commits.length === 0 ? (
          <p className="text-fg-tertiary text-xs italic">No commits since {baseTag}.</p>
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>PR / SHA</Table.HeaderCell>
                <Table.HeaderCell>CI</Table.HeaderCell>
                <Table.HeaderCell>Subject</Table.HeaderCell>
                <Table.HeaderCell>Author</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {[...commits].reverse().map((commit) => (
                <Table.Row key={commit.sha}>
                  <Table.Cell muted>
                    <Tooltip title={formatPacificDateTime(commit.authoredAt)} placement="top">
                      <TooltipTrigger>
                        <span>{formatTimeAgo(commit.authoredAt)}</span>
                      </TooltipTrigger>
                    </Tooltip>
                  </Table.Cell>
                  {/*
                    Both identifiers, because they answer different questions: the
                    PR is where the change is discussed, the sha is what the pin,
                    the audit footer, and `git log` are all keyed on.
                  */}
                  <Table.Cell mono>
                    {commit.prUrl && commit.prNumber != null && (
                      <a
                        href={commit.prUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-fg-brand-primary hover:text-fg-brand-primary_alt underline underline-offset-2"
                      >
                        #{commit.prNumber}
                      </a>
                    )}{' '}
                    <span className="text-fg-tertiary">{commit.shortSha}</span>
                  </Table.Cell>
                  <Table.Cell className="w-8 text-center">
                    <CommitCiIndicator conclusion={commit.ciConclusion} />
                  </Table.Cell>
                  <Table.Cell>{commit.subject}</Table.Cell>
                  <Table.Cell mono muted>
                    {commit.author ?? '—'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ))}
    </div>
  );
};

export default CommitsBlock;
